/*
 * Copyright (c) 2023. Data Smart Technologies CC
 * https://www.datasmart.co.za
 * View license agreement and terms of use at https://www.datasmart.co.za/legal
 *
 * @author  Juan <juan@datasmart.co.za>
 * @version 1.0
 * @updated 2023-07-11
 */

import React, { useEffect, useState } from "react";
import { SpinnerLoading } from "../utils/SpinnerLoading";
import { ErrorMessage } from "../utils/ErrorMessage";
import { fetchRecords } from "../utils/AppUtil";
import { Link } from "react-router-dom";

class Funeral {
    deceasedId: string;
    burialDate: string;
    burialTime: string;
    deceased: string;
    cemeteryName: string;
    constructor(deceasedId: string, burialDate: string, burialTime: string, deceased: string, cemeteryName: string) {
        this.deceasedId = deceasedId;
        this.burialDate = burialDate;
        this.burialTime = burialTime;
        this.deceased = deceased;
        this.cemeteryName = cemeteryName; 
    }
}

type RecentFuneralsProps = {
    endpoint: string;
}

export const RecentFunerals: React.FC<RecentFuneralsProps> = (props) => {
    
    const [records, setRecords] = useState<Funeral[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [httpError, setHttpError] = useState(null);
    const [endpoint, setEndpoint] = useState(`${props.endpoint}`);

    // Do not change this or put any additional code in here without checking with
    // the team first.
    useEffect(() => {

        fetchRecords(endpoint, processData)
        .catch((error) => {
            setIsLoading(false);
            setHttpError(error.message);
        });

    }, [endpoint]);

    // You may edit this function to manipulate the way your code is processed.
    const processData = (responseJson: any) => { // Do not edit these parameters, just the content of this function.
        const recordData: Funeral[] = [];
    
        for(const key in responseJson) {
            const record: any = responseJson[key];
            const deceasedId = record["deceasedId"];
            const burialDate: string = record["burialDateDisplay"];
            const burialTime: string = record["burialTimeDisplay"];
            const deceased: string = (record["firstName"] ? record["firstName"] : "") + " " + (record["lastName"] ? record["lastName"] : "");
            const cemeteryName: string = record["cemeteryName"];

            recordData.push(new Funeral(deceasedId,burialDate, burialTime, deceased, cemeteryName));
        }
        
        setRecords(recordData);
        setIsLoading(false);
    }

    if(isLoading) {
        return(
            <SpinnerLoading />
        )
    }

    if(httpError) {
        return(
            <ErrorMessage error={httpError} />
        )
    }    
    return (
        <div className="card card-recent-funerals jc-border-12">
            <div className="card-header card-header-bg">
                <p className="jc-txt-heading fs-5">Recent Funerals</p>
            </div>

            <div className="card-body p-0">
                <div className="table-recent-funerals">
                    <table className="table table-striped mb-0">
                        <tbody>
                            <RecentFuneralRecords records={records} />
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

const RecentFuneralRecords: React.FC<{records: Funeral[];}>= ({records}): JSX.Element => {

    const result: React.ReactElement[] = [];

    records.forEach((funeral, index) => {
        result.push(
            <tr key={index}>
                <td className="jc-txt-body fs-6 fw-bold table-mobile-left">
                    <Link to={'/deceased/' + funeral.deceasedId} className="jc-txt-blue">{funeral.deceased}</Link>
                </td>

                <td className="jc-txt-body fs-7 align-end table-mobile-right" style={{whiteSpace: "nowrap"}}>
                    <span>{funeral.burialDate}</span>
                    <span>{funeral.cemeteryName ? " • " + funeral.cemeteryName : ""}</span>
                    <span>{funeral.burialTime  ? " • " + funeral.burialTime : ""}</span>
                </td>
            </tr>
        )
    });

    return(
        <>{result}</>
    );
}
